'use client';
// The hero finder (city, occasion, when → /reserve) and the city menu it shares with /reserve.
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { getLocalizedPath } from '@/lib/getLocalizedPath';
import type { City, Occasion } from '@/lib/v9/data';
import type { DesignOccasion } from '@/lib/v9/designOccasions';
import { Arrow, Check, Pin, Sparkle, OccasionIcon } from './icons';
import { startRouteProgress } from './RouteProgress';
import { cx, useEscape, useLang } from './ui';

const CITY_KEY = 'mila.city';

export function readSavedCity(): string | null {
  try { return localStorage.getItem(CITY_KEY); } catch { return null; }
}
export function saveCity(id: string) {
  try { localStorage.setItem(CITY_KEY, id); } catch { /* private mode */ }
}

export function CityMenu({ cities, value, onChange, tone = 'paper', className }: {
  cities: City[]; value: string | null; onChange: (id: string) => void; tone?: 'paper' | 'ink'; className?: string;
}) {
  const { pick } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEscape(open ? () => setOpen(false) : null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const cur = cities.find((c) => c.id === value) ?? cities[0];
  return (
    <div ref={ref} className={cx('relative inline-block', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          'inline-flex min-h-[44px] items-center gap-2 rounded-full pe-3 ps-3.5 text-[15px] font-extrabold',
          tone === 'ink' ? 'bg-white/[.12] text-white shadow-[inset_0_0_0_1px_rgba(240,213,140,.55)]' : 'bg-panel text-deep shadow-[inset_0_0_0_1px_rgba(217,180,95,.7)]',
        )}
      >
        <Pin className={cx('h-[18px] w-[18px] flex-none', tone === 'ink' ? 'text-sun' : 'text-plum')} />
        {cur ? pick(cur.name_ar, cur.name_en) : '—'}
        <i className={cx('h-[9px] w-[9px] border-b-2 border-e-2 border-current opacity-80 transition-transform', open ? 'translate-y-[3px] rotate-[225deg]' : '-translate-y-[3px] rotate-45')} />
      </button>
      {open && (
        <ul role="listbox" className="absolute start-0 top-[calc(100%+8px)] z-30 m-0 min-w-[200px] list-none rounded-[18px] bg-white p-1.5 text-deep shadow-[0_0_0_1px_rgba(217,180,95,.8),0_24px_44px_-20px_rgba(30,10,46,.6)] animate-[v9rise_.22s_var(--ease)]">
          {cities.map((c) => (
            <li
              key={c.id}
              role="option"
              aria-selected={c.id === cur?.id}
              tabIndex={0}
              onClick={() => { onChange(c.id); setOpen(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(c.id); setOpen(false); } }}
              className={cx('flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-xl px-3.5 py-1.5 text-[15px] hover:bg-panel', c.id === cur?.id ? 'font-extrabold text-plum' : 'font-bold')}
            >
              {pick(c.name_ar, c.name_en)}
              {c.id === cur?.id && <Check className="h-4 w-4" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Occasion chips: icon circles with a label; "regular" is null. */
export function OccasionChips({ occasions, value, onChange, regularLabel, tone = 'paper', className }: {
  /** undefined: nothing picked yet; null: a regular booking. */
  occasions: Occasion[]; value: string | null | undefined; onChange: (id: string | null) => void; regularLabel: string; tone?: 'paper' | 'ink'; className?: string;
}) {
  const { pick } = useLang();
  const chip = (id: string | null, label: string, icon: React.ReactNode) => {
    const on = value === id;
    return (
      <button
        key={id ?? 'regular'}
        type="button"
        aria-pressed={on}
        onClick={() => onChange(id)}
        className={cx('flex min-w-0 flex-col items-center gap-1.5 px-0.5 py-1 text-center text-[11.5px] font-bold leading-[1.3]',
          tone === 'ink' ? (on ? 'text-white' : 'text-white/[.82]') : (on ? 'text-deep' : 'text-deep/[.78]'))}
      >
        <i className={cx('grid h-[54px] w-[54px] place-items-center rounded-full transition-[background-color,box-shadow,transform] duration-200',
          on ? 'scale-[1.06] bg-sun text-deep shadow-[0_0_0_3px_rgba(240,213,140,.4)]'
            : tone === 'ink' ? 'bg-white/10 shadow-[inset_0_0_0_1px_rgba(240,213,140,.5)]' : 'bg-panel text-plum shadow-[inset_0_0_0_1px_rgba(217,180,95,.7)]')}>
          {icon}
        </i>
        <span className="line-clamp-2">{label}</span>
      </button>
    );
  };
  return (
    <div className={cx('mt-1 grid gap-1.5', className)} style={{ gridTemplateColumns: `repeat(${Math.min(occasions.length + 1, 5)}, minmax(0, 1fr))` }}>
      {occasions.slice(0, 4).map((o) => chip(o.id, pick(o.name_ar, o.name_en),
        'key' in o ? <OccasionIcon kind={(o as DesignOccasion).key} className="h-6 w-6" />
          : o.icon ? <img src={o.icon} alt="" className="h-6 w-6 object-contain" /> : <Sparkle className="h-5 w-5" />))}
      {chip(null, regularLabel, <OccasionIcon kind="regular" className="h-6 w-6" />)}
    </div>
  );
}

export function HeroFinder({ cities, occasions, defaultCityId }: { cities: City[]; occasions: Occasion[]; defaultCityId: string | null }) {
  const t = useTranslations('v9.home');
  const { locale, pick } = useLang();
  const router = useRouter();
  const [cityId, setCityId] = useState<string | null>(defaultCityId);
  const [occ, setOcc] = useState<string | null | undefined>(undefined);
  const [when, setWhen] = useState<'today' | 'weekend' | null>(null);
  useEffect(() => {
    const saved = readSavedCity();
    if (saved && cities.some((c) => c.id === saved)) setCityId(saved);
  }, [cities]);
  const city = cities.find((c) => c.id === cityId) ?? null;
  const target = (map = false) => {
    const q = new URLSearchParams();
    if (cityId) q.set('city', cityId);
    if (occ) q.set('occasion', occ);
    if (when) q.set('when', when);
    if (map) q.set('map', '1');
    return getLocalizedPath(locale, '/reserve') + (q.toString() ? `?${q}` : '');
  };
  // The list is prerendered and the same for every filter (ReserveClient applies the query in the browser): fetched
  // ahead once, when the home is idle, so the click opens it at once.
  useEffect(() => {
    const id = window.setTimeout(() => router.prefetch(getLocalizedPath(locale, '/reserve')), 1500);
    return () => window.clearTimeout(id);
  }, [locale, router]);
  // The button shows it is working until the list page takes over (the transition ends when the new page renders).
  const [pending, startTransition] = useTransition();
  const go = (map = false) => {
    startRouteProgress();
    startTransition(() => router.push(target(map)));
  };
  return (
    <div className="mt-3 grid w-full gap-2.5 rounded-[26px] bg-white/[.96] p-2.5 text-deep shadow-[0_0_0_1px_rgba(240,213,140,.9),0_46px_80px_-44px_rgba(0,0,0,.85)] lg:w-[min(100%,520px)]">
      <div className="flex items-center justify-between gap-2.5 px-1.5 pt-0.5">
        <small className="text-[13px] font-bold text-deep/[.62]">{t('where')}</small>
        <CityMenu cities={cities} value={cityId} onChange={(id) => { setCityId(id); saveCity(id); }} />
      </div>
      <OccasionChips occasions={occasions} value={occ} onChange={setOcc} regularLabel={t('regular')} className="px-0.5" />
      <div className="flex gap-2">
        {(['today', 'weekend'] as const).map((w) => (
          <button
            key={w}
            type="button"
            aria-pressed={when === w}
            onClick={() => setWhen(when === w ? null : w)}
            className={cx('min-h-[44px] flex-1 rounded-[14px] text-[13.5px] font-extrabold', when === w ? 'bg-plum text-white' : 'bg-[#F6F0FB] text-plum shadow-[inset_0_0_0_1.5px_rgba(85,39,109,.35)]')}
          >
            {w === 'today' ? t('tonight') : t('weekend')}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => go()}
        aria-busy={pending}
        disabled={pending}
        className={cx(
          'inline-flex min-h-[58px] items-center justify-center gap-2.5 rounded-[18px] bg-plum px-[22px] text-[17px] font-extrabold text-white shadow-[0_0_0_1px_rgba(217,180,95,.7),0_18px_30px_-16px_rgba(85,39,109,.9)] transition-[background-color,transform,opacity] duration-200 hover:bg-deep active:scale-[.985]',
          pending && 'cursor-progress bg-deep',
        )}
      >
        {pending ? (
          <>
            <span aria-hidden="true" className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/30 border-t-sun" />
            {t('opening')}
          </>
        ) : (
          <>
            {city ? t('browseIn', { city: pick(city.name_ar, city.name_en) }) : t('browse')}
            <Arrow className="h-[18px] w-[18px]" />
          </>
        )}
      </button>
      <div className="flex flex-wrap justify-between gap-x-3.5 gap-y-1.5 px-1.5 pb-1 pt-0.5">
        <button type="button" onClick={() => go(true)} disabled={pending} className="inline-flex min-h-[44px] items-center text-[13.5px] font-bold text-plum underline decoration-gold/90 underline-offset-4 disabled:opacity-60">{t('orMap')}</button>
      </div>
    </div>
  );
}
